import { QuotaModel } from "../../../models/QuotaModel";
import { InterviewState } from "../../../models/states/InterviewState";
import { DispositionCode } from "../../../models/enums/DispositionCode";
import { __evaluateSafe__ } from "../../../expressions/parser";
import { QuotaState } from "../../../models/states/QuotaState";
import { QuotaStatus } from "../../../models/enums/QuotaStatus";
import { ActionTypes } from "../../actions/types";
import {
    QUOTAS_CHANGED,
    NAVIGATION_NEXT_PAGE,
    QUOTAS_CLOSE_STRATUM,
    QUOTAS_CLOSE_USER_STRATUM,
} from "../../actions";

const INITIAL_STATE: QuotaState = {
    input: [],
    output: [],
};

const initializeStrata = (input: QuotaModel[]) =>
    input.map((quota) => {
        const output = {
            ...quota,
            // Set closed status to half-open for closed strata warning dialogs.
            status: quota.status === QuotaStatus.Open ? QuotaStatus.Open : QuotaStatus.HalfOpen,
            userStatus:
                quota.userStatus === QuotaStatus.Open ? QuotaStatus.Open : QuotaStatus.HalfOpen,
        };
        // If the quota is global objective, increment right away.
        if (quota.stratumId === 0) {
            output.frequency += 1;
            output.userFrequency += 1;
        }
        return output;
    });

const decrementIfResumed = (input: QuotaModel[], interview: InterviewState) =>
    input.map((quota) => {
        if (interview.respondent.dispositionCode === DispositionCode.Empty) return quota; // New respondent; do not decrement.
        if (quota.stratumId > 0 && !__evaluateSafe__(interview, quota.condition).asBoolean())
            return quota; // Quota was already false when resumed; do not decrement.
        return {
            ...quota,
            frequency: quota.frequency - 1,
            userFrequency: quota.userFrequency - 1,
        };
    });

const calculateStrata = (input: QuotaModel[], output: QuotaModel[], interview: InterviewState) =>
    input.map((quota, index) => {
        if (quota.stratumId > 0 && !__evaluateSafe__(interview, quota.condition).asBoolean())
            return quota; // Only increment quota if it is global or evaluates as true.
        return {
            ...quota,
            frequency: quota.frequency + 1,
            userFrequency: quota.userFrequency + 1,
            status: output[index].status,
            userStatus: output[index].userStatus,
        };
    });

const closeStratum = (input: QuotaModel[], stratumId: number) =>
    input.map((quota) => {
        if (quota.stratumId !== stratumId) return quota;
        return {
            ...quota,
            status: QuotaStatus.Closed,
            userStatus: QuotaStatus.Closed,
        };
    });

const closeUserStratum = (input: QuotaModel[], stratumId: number) =>
    input.map((quota) => {
        if (quota.stratumId !== stratumId) return quota;
        return {
            ...quota,
            userStatus: QuotaStatus.Closed,
        };
    });

export const quotasReducer = (
    state = INITIAL_STATE,
    interview: InterviewState,
    action: ActionTypes,
): QuotaState => {
    switch (action.type) {
        case QUOTAS_CHANGED:
            return {
                input: decrementIfResumed(action.quotas, interview),
                output: initializeStrata(action.quotas),
            };
        case NAVIGATION_NEXT_PAGE:
            return {
                ...state,
                output: calculateStrata(state.input, state.output, interview),
            };
        case QUOTAS_CLOSE_STRATUM:
            return {
                ...state,
                output: closeStratum(state.input, action.stratumId),
            };
        case QUOTAS_CLOSE_USER_STRATUM:
            return {
                ...state,
                output: closeUserStratum(state.input, action.stratumId),
            };
        default:
            return state;
    }
};
